import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
  Card, Icon, message, Tabs, Timeline
} from 'antd';
import _ from 'lodash';
import { Prompt } from 'components/Page';
import { add, update } from 'actions';
import REDUCER_KEY from 'reducers/reducer.keys';

const { TabPane } = Tabs;

const mapStateToProps = state => ({
  roster: state.api.rosterVersion.data
});

const mapDispatchToProps = dispatch => ({
  saveRoster(roster, defaultSettings) {
    const { id, parent_roster: parent } = roster;
    // build data for filters and roster custom filter state
    const params = {
      id,
      parent_roster_id: parent.id,
      default_settings: {
        filters: { ...defaultSettings.filters },
        options: { ...defaultSettings.options },
        customFilters: { ...defaultSettings.customFilters }
      }
    };
    return dispatch(id < 0 || id === null ? add(REDUCER_KEY.rosterVersion.key, params) : update(REDUCER_KEY.rosterVersion.key, params));
  }
});
const SettingsWrapper = (WrappedComponent) => {
  /**
   * @description Get the status icon for the Tab
   * @param {defaultSettings} object
   * @param {settings} object
   * @return {Icon} element
   */
  const getTabIcon = isEqual => (isEqual
    ? <Icon type="check-circle" />
    : <Icon type="exclamation-circle" />);

  /**
   * @description Get the Colour status
   * @param {isEqual} bool
   * @return {string}
   */
  const getLineColour = isEqual => (isEqual
    ? 'green'
    : 'red');

  /**
   * @description Get Icon indicator for a Line
   * @param {isEqual} bool
   * @return {Icon} element
   */
  const getLineIcon = isEqual => (isEqual
    ? <Icon type="check-circle" />
    : <Icon type="clock-circle-o" />);

  /**
   * @description Get Timeline Line
   * @param {isEqual} bool
   * @param {content} Object
   * @return {Timeline.Item element}
   */
  const getLine = (isEqual, content) => (
    <Timeline.Item color={getLineColour(isEqual)} dot={getLineIcon(isEqual)}>{content}</Timeline.Item>
  );

  /**
   * @description Make first character capital
   * @param {string} title
   * @return {string}
   */
  const prepareTitle = title => title.charAt(0)
    .toUpperCase() + title.slice(1);

  /**
   * @description Find differences in objects
   * @param {object} object
   * @param {object} base
   * @fires {changes}
   * @return {object}
   */
  const getDifference = (object, base) => {
    function changes(objectIn, baseIn) {
      return _.transform(objectIn, (result, value, key) => {
        if (!_.isEqual(value, baseIn[key])) {
          // eslint-disable-next-line
          result[key] = (_.isObject(value) && _.isObject(baseIn[key])) ? changes(value, baseIn[key]) : value;
        }
      });
    }

    return changes(object, base);
  };

  /**
   * @description Join Arrays or display
   * @param {object} content
   * @return {string}
   */
  const transformContent = content => (Array.isArray(content) ? content.join(' | ') : content);

  /**
   * @description Build the TabsPane & Timeline content
   * @param {object} original
   * @param {object} current
   * @param {string} title
   * @param {object} display
   * @param {number} index
   * @return {object}
   */
  const getContent = (original, current, title, display, index) => {
    const difference = getDifference(current, original);
    const isEqual = _.isEmpty(difference);
    // build original settings content
    const originalContent = Object.keys(difference)
      .map((setting) => {
        if (!display[setting]) return null;
        return (
          <div key={display[setting].title}>
            <strong>{display[setting].title}</strong>
            {' '}
            {transformContent(display[setting].data(original[setting]))}
          </div>
        );
      });
    // build current settings content
    const currentContent = Object.keys(difference)
      .map((setting) => {
        if (!display[setting]) return null;
        return (
          <div key={display[setting].title}>
            <strong>{display[setting].title}</strong>
            {' '}
            {transformContent(display[setting].data(current[setting]))}
          </div>
        );
      });

    return (
      <TabPane
        tab={(
          <span>
            {getTabIcon(isEqual)}
            {' '}
            {prepareTitle(title)}
          </span>
        )}
        key={index}
      >
        <Card>
          {isEqual && <div>No changes made</div>}
          {!isEqual && (
            <Timeline mode="alternate">
              {getLine(true, originalContent)}
              {getLine(isEqual, currentContent)}
            </Timeline>
          )}
        </Card>
      </TabPane>
    );
  };

  /**
   * @description Launch the Prompt to Save or Discard changes
   * @param {function} save
   * @param {function} callback
   * @param {object} settings
   */
  const promptSaveChanges = (save, callback, settings) => {
    const { defaultSettings, currentSettings } = settings;
    let index = 0;
    return Prompt({
      content: (
        <div>
          <p>
            You have made changes to the settings. Below is a comparison of these settings.
          </p>
          <p>
            Do you wish to save these changes?
          </p>
          <Tabs tabPosition="top">
            {
              Object.keys(defaultSettings.filters)
                .map(setting => getContent(defaultSettings.filters[setting], currentSettings.filters[setting], `${setting} Filter`, defaultSettings.display, ++index))
            }
            {defaultSettings.options && getContent(defaultSettings.options, currentSettings.options, 'Options', defaultSettings.display, ++index)}
          </Tabs>
        </div>
      ),
      onOkClick: () => save(settings.currentSettings, callback),
      onCancelClick: () => {
      },
      okText: 'Yes - Save New Default Settings',
      cancelText: 'No - Cancel Save',
      title: 'Save New Default Settings ?'
    });
  };

  const HOC = ({
    roster, saveRoster, dispatch, ...rest
  }) => {
    /**
     * @description Process the adding and removing of Tags
     * @param {objectId} number
     * @param {tags} array
     * @param {originalTags} array
     * @return {Promise}
     */
    const saveSettings = (settings, callback) => saveRoster(roster, settings)
      .then(
        () => {
          message.success('Settings saved succesfully');
          callback(); // execute callback
        }
      )
      .catch(
        response => message.error(`Settings failed to save: ${response}`)
      );

    /**
     * @description Call the prompt and pass data
     * @param {settings} Object
     * @param {callback} function
     * @return {Object}
     */
    const updateSettings = (settings, callback = () => {
    }) => promptSaveChanges(saveSettings, callback, settings);
    return (
      <WrappedComponent
        {...rest}
        onSaveSettings={updateSettings}
      />
    );
  };

  HOC.propTypes = {
    dispatch: PropTypes.func,
    roster: PropTypes.object,
    saveRoster: PropTypes.func
  };

  HOC.defaultProps = {
    dispatch() {
    },
    roster: {},
    saveRoster() {
    }
  };

  return HOC;
};

const Settings = compose(
  connect(mapStateToProps, mapDispatchToProps), SettingsWrapper
);

export default Settings;
